import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const ProfilPesantren = () => {
  const { session } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedLogoUrl, setSignedLogoUrl] = useState(null);
  const [isOpeningDoc, setIsOpeningDoc] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileData = async () => {
      if (!session?.user?.id) return;

      try {
        setIsLoading(true);

        // Fetch data menggunakan Relational Join Supabase
        const { data: regData, error: regError } = await supabase
          .from('registrations')
          .select(`
                        *,
                        master_provinces (name),
                        master_cities (name)
                    `)
          .eq('user_id', session.user.id)
          .single();

        if (regError) throw regError;

        if (isMounted) {
          setProfile({
            ...regData,
            province_name: regData.master_provinces?.name || 'Data tidak ditemukan',
            city_name: regData.master_cities?.name || 'Data tidak ditemukan'
          });
        }

      } catch (err) {
        console.error('Error fetching profile:', err);
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchProfileData();

    return () => { isMounted = false; };
  }, [session]);

  // Generate Signed URL untuk Logo saat profile tersedia
  useEffect(() => {
    if (!profile?.logo_url) return;

    const generateLogoSignedUrl = async () => {
      try {
        // 1. Bersihkan query parameters
        const cleanUrl = profile.logo_url.split('?')[0];

        // 2. Ekstrak path menggunakan separator /public/registration_logos/
        const bucketName = 'registration_logos';
        const separator = `/public/${bucketName}/`;
        let filePath = '';

        if (cleanUrl.includes(separator)) {
          filePath = cleanUrl.split(separator)[1];
        } else {
          filePath = cleanUrl.split('/').pop();
        }

        // 3. Decode URL (%20 → spasi, dll.)
        const decodedPath = decodeURIComponent(filePath);

        // 4. Request Signed URL dari bucket privat
        const { data, error } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(decodedPath, 3600); // 1 jam validity

        if (error) throw error;
        setSignedLogoUrl(data.signedUrl);
      } catch (err) {
        console.error('Error generating logo signed URL:', err);
        // Tidak perlu alert, logo hanya tidak tampil
      }
    };

    generateLogoSignedUrl();
  }, [profile]);

  // Handler untuk membuka dokumen legalitas dari bucket privat
  const handleViewDocument = async (rawUrl) => {
    if (!rawUrl) return alert('Data URL dokumen tidak ditemukan.');

    try {
      setIsOpeningDoc(true);

      // 1. Bersihkan query parameters
      const cleanUrl = rawUrl.split('?')[0];

      // 2. Ekstrak path menggunakan separator /public/registration_docs/
      const bucketName = 'registration_docs';
      const separator = `/public/${bucketName}/`;
      let filePath = '';

      if (cleanUrl.includes(separator)) {
        filePath = cleanUrl.split(separator)[1];
      } else {
        filePath = cleanUrl.split('/').pop();
      }

      // 3. Decode URL (%20 → spasi, dll.)
      const decodedPath = decodeURIComponent(filePath);

      // 4. Request Signed URL dari bucket privat
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(decodedPath, 60);

      if (error) throw error;
      window.open(data.signedUrl, '_blank');

    } catch (err) {
      console.error('Error generating document signed URL:', err);
      alert(`Gagal membuka dokumen:\n${err.message}`);
    } finally {
      setIsOpeningDoc(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <p className="text-gray-500 animate-pulse">Memuat profil pesantren...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          Gagal memuat data: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Profil Pesantren</h1>
          <p className="text-sm text-gray-500">Informasi identitas dan keanggotaan HEBITREN.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start gap-8">
            {/* Logo Area */}
            <div className="w-32 h-32 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
              {signedLogoUrl ? (
                <img
                  src={signedLogoUrl}
                  alt={`Logo ${profile?.pesantren_name}`}
                  className="w-full h-full object-cover"
                />
              ) : profile?.logo_url ? (
                <span className="text-gray-400 text-xs text-center px-2">Memuat logo...</span>
              ) : (
                <span className="text-gray-400 text-sm">Tidak ada logo</span>
              )}
            </div>

            {/* Info Area */}
            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Nama Pesantren</h3>
                <p className="mt-1 text-base font-semibold text-gray-900">{profile?.pesantren_name || '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Kode Tracking</h3>
                <p className="mt-1 text-base text-gray-900 font-mono bg-gray-100 px-2 py-0.5 rounded inline-block">
                  {profile?.tracking_code || '-'}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Pimpinan Pesantren</h3>
                <p className="mt-1 text-base text-gray-900">{profile?.leader_name || '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Penanggung Jawab Pesantren</h3>
                <p className="mt-1 text-base text-gray-900">{profile?.pic_hebitren_name || '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Kontak</h3>
                <p className="mt-1 text-base text-gray-900">{profile?.contact_number || '-'}</p>
                <p className="text-sm text-gray-600">{profile?.email || '-'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Lokasi</h3>
                <p className="mt-1 text-base text-gray-900">
                  {profile?.city_name}, {profile?.province_name}
                </p>
              </div>

              <div className="sm:col-span-2 pt-4 border-t border-gray-100 mt-2">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Dokumen Legalitas</h3>
                {profile?.document_url ? (
                  <button
                    onClick={() => handleViewDocument(profile.document_url)}
                    disabled={isOpeningDoc}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-wait"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {isOpeningDoc ? 'Membuka...' : 'Lihat Dokumen'}
                  </button>
                ) : (
                  <span className="text-sm text-gray-500">-</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilPesantren;